import { useMutation, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { useAuth } from "@/lib/auth/AuthProvider";
import { apiFetch } from "@/lib/api/client";

export const SUPPLEMENTS_EXPERIMENT_KEY = "portal-supplements-shop";

export interface SupplementProduct {
  id: string;
  title: string;
  brand: string;
  slug: string;
  asin: string;
  category: string;
  shortDescription: string;
  description?: string | null;
  form?: string | null;
  size?: string | null;
  servingInfo?: string | null;
  ingredients?: string | null;
  cautions: string[];
  qualityNotes: string[];
  imageUrl: string | null;
  amazonUrl: string;
  priceCents: number | null;
  currency: string;
  available: boolean;
  priceCheckedAt: string | null;
  priceStatus?: "current" | "refreshing" | "unavailable";
}

export interface SupplementCatalog {
  enabled: boolean;
  checkoutEnabled: boolean;
  pricesRefreshing?: boolean;
  products: SupplementProduct[];
  experiment: { codeKey: string; key: string; variantKey: string } | null;
}

export interface SupplementPurchaseRequest {
  cart_promotion_id?: string;
  items: {
    productId: string;
    quantity: number;
    expectedUnitPriceCents: number;
    cart_promotion_line_key?: string;
  }[];
  paymentMethodId: string;
  idempotencyKey: string;
}

export interface SupplementPurchaseResult {
  publishableKey?: string;
  items: {
    productId: string;
    orderId: string;
    quantity: number;
    totalCents: number;
    status:
      | "succeeded"
      | "requires_action"
      | "processing"
      | "failed"
      | "cancelled";
    paymentIntentId?: string;
    clientSecret?: string;
    error?: { code: string; message: string };
  }[];
}

export interface SupplementOrder {
  id: string;
  productTitle: string;
  quantity: number;
  totalCents: number;
  status: string;
  createdAt?: string;
  tracking?: { carrier?: string; trackingNumber?: string; status?: string }[];
}

export function useSupplementCatalog(enabled = true) {
  const { session } = useAuth();
  const refreshWindow = useRef<{ userId: string; startedAt: number } | null>(
    null,
  );
  return useQuery({
    queryKey: ["supplements", session?.user.id, "catalog"],
    enabled: enabled && Boolean(session?.user.id),
    queryFn: () => apiFetch<SupplementCatalog>("/supplements/catalog"),
    staleTime: 60_000,
    retry: false,
    refetchInterval: (query) => {
      const waitingForFirstPrice =
        query.state.data?.pricesRefreshing ||
        query.state.data?.products.some(
          (product) =>
            product.priceCents == null && product.priceStatus === "refreshing",
        );
      if (!waitingForFirstPrice || !session?.user.id) {
        refreshWindow.current = null;
        return false;
      }
      if (refreshWindow.current?.userId !== session.user.id) {
        refreshWindow.current = {
          userId: session.user.id,
          startedAt: Date.now(),
        };
      }
      // Poll the fast database response briefly while the background worker
      // fills a cold cache. A provider outage must not cause endless polling.
      return Date.now() - refreshWindow.current.startedAt < 120_000
        ? 5_000
        : false;
    },
  });
}

export function useSupplementPurchase() {
  return useMutation({
    mutationFn: (body: SupplementPurchaseRequest) =>
      apiFetch<SupplementPurchaseResult>("/supplements/purchase", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  });
}

export function useSupplementOrders() {
  const { session } = useAuth();
  return useQuery({
    queryKey: ["supplements", session?.user.id, "orders"],
    enabled: Boolean(session?.user.id),
    queryFn: () =>
      apiFetch<{ orders: SupplementOrder[] }>("/supplements/orders"),
    staleTime: 15_000,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.orders.some((order) =>
        [
          "pending_payment",
          "queued",
          "submitted",
          "placed",
          "shipped",
          "refund_pending",
        ].includes(order.status),
      )
        ? 15_000
        : false,
  });
}
